/**
 * Master Data Contact API Service
 * Service layer for standalone contact management API calls
 * Handles both vendor and customer contacts
 *
 * B2.6'-fe-seam: `list` gains a live-module leg (the `masters` key,
 * client.ts) executing the ContactListPage contract operation against
 * `/graphql` (masters.operations.ts → masters.gql-map.ts). Signature
 * unchanged. `getById` stays on the mock seam in BOTH states — its legacy
 * route is vendor/customer-scoped (entityId + type) while the contract
 * models one `masterDataContact(id:)` root; the consumer-side id migration
 * is CU-1 flip scope (B2.7'). Writes likewise. L2 live leg XFAIL until the
 * flip.
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import {
  GraphQLTransportError,
  gqlMutate,
  gqlRequest,
  throwOnUserErrors,
} from './graphql.client';
import {
  mastersOperations,
  type ContactListPageResult,
  type CustomerContactSetActiveResult,
  type MastersListVariables,
  type VendorContactSetActiveResult,
} from './masters.operations';
import {
  listConstraintsDroppedOnLive,
  mapContactListNode,
  mapPartyContactToggleEcho,
  toFirstVariable,
  toLegacyPagination,
} from './masters.gql-map';
import type {
  MasterDataContactListResponse,
  MasterDataContactListParams,
  MasterDataContactDetail,
  CreateVendorContactRequest,
  CreateCustomerContactRequest,
  UpdateMasterDataContactRequest,
  ToggleMasterDataContactResponse,
  SetPrimaryMasterDataContactResponse,
  ContactType,
  VendorListItem,
  CustomerListItem,
} from '@/types/api/masterDataContact';
import { normalizeContactAffiliations } from '@/types/api/masterDataContact';
import type { ContactDetailResponse } from '@/types/api/contact';

/**
 * API Routes
 */
const ROUTES = {
  // Master data contacts list
  CONTACTS: '/contacts',
  // Vendor contacts
  VENDOR_CONTACTS: (vendorId: number) => `/vendors/${vendorId}/contacts`,
  VENDOR_CONTACT: (vendorId: number, contactId: number) =>
    `/vendors/${vendorId}/contacts/${contactId}`,
  VENDOR_CONTACT_TOGGLE: (vendorId: number, contactId: number) =>
    `/vendors/${vendorId}/contacts/${contactId}/toggle`,
  VENDOR_CONTACT_SET_PRIMARY: (vendorId: number, contactId: number) =>
    `/vendors/${vendorId}/contacts/${contactId}/set-primary`,
  // Customer contacts
  CUSTOMER_CONTACTS: (customerId: number) => `/customers/${customerId}/contacts`,
  CUSTOMER_CONTACT: (customerId: number, contactId: number) =>
    `/customers/${customerId}/contacts/${contactId}`,
  CUSTOMER_CONTACT_TOGGLE: (customerId: number, contactId: number) =>
    `/customers/${customerId}/contacts/${contactId}/toggle`,
  CUSTOMER_CONTACT_SET_PRIMARY: (customerId: number, contactId: number) =>
    `/customers/${customerId}/contacts/${contactId}/set-primary`,
  // Vendors/Customers lists for dropdowns
  VENDORS: '/vendors',
  CUSTOMERS: '/customers',
};

/**
 * Master Data Contact API service object
 */
export const masterDataContactApi = {
  /**
   * GET /api/contacts
   * Fetch list of all contacts (both vendor and customer)
   */
  list: async (params: MasterDataContactListParams): Promise<MasterDataContactListResponse> => {
    if (isLiveModule('masters')) {
      const dropped = listConstraintsDroppedOnLive(params);
      if (dropped.length > 0) {
        console.warn(
          `[masterDataContactApi.list] live leg drops [${dropped.join(', ')}] until the CU-1 flip plumbs filter/sort/cursor variables`
        );
      }
      const result = await gqlRequest<ContactListPageResult, MastersListVariables>(
        mastersOperations.contactList,
        { first: toFirstVariable(params.pageSize), after: null }
      );
      return {
        data: result.masterDataContacts.edges.map((edge) => mapContactListNode(edge.node)),
        pagination: toLegacyPagination(result.masterDataContacts.totalCount, params.pageSize),
      };
    }
    const response = await apiClient.get<MasterDataContactListResponse>(ROUTES.CONTACTS, {
      params,
    });
    // #92 transform layer: guarantee the multi-affiliation list on every row —
    // legacy single-org records map to a one-element list
    return {
      ...response.data,
      data: response.data.data.map((row) => ({
        ...row,
        affiliations: normalizeContactAffiliations(row),
      })),
    };
  },

  /**
   * Get single contact by ID
   * Routes to vendor or customer endpoint based on type
   */
  getById: async (
    contactId: number,
    entityId: number,
    type: ContactType
  ): Promise<MasterDataContactDetail> => {
    const route =
      type === 1
        ? ROUTES.VENDOR_CONTACT(entityId, contactId)
        : ROUTES.CUSTOMER_CONTACT(entityId, contactId);

    const response = await apiClient.get<
      ContactDetailResponse & Pick<MasterDataContactDetail, 'affiliations'>
    >(route);

    // Map to MasterDataContactDetail
    const detail: MasterDataContactDetail = {
      ...response.data,
      vendorId: type === 1 ? entityId : null,
      customerId: type === 2 ? entityId : null,
    };
    // #92 transform layer: legacy single-org records map to a one-element list
    return {
      ...detail,
      affiliations: normalizeContactAffiliations(detail),
    };
  },

  /**
   * Create vendor contact
   * POST /api/vendors/:vendorId/contacts
   */
  createVendorContact: async (
    vendorId: number,
    data: Omit<CreateVendorContactRequest, 'vendorId'>
  ): Promise<ContactDetailResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.createVendorContact');
    const requestData: CreateVendorContactRequest = {
      ...data,
      vendorId,
    };

    const response = await apiClient.post<ContactDetailResponse>(
      ROUTES.VENDOR_CONTACTS(vendorId),
      requestData
    );
    return response.data;
  },

  /**
   * Create customer contact
   * POST /api/customers/:customerId/contacts
   */
  createCustomerContact: async (
    customerId: number,
    data: Omit<CreateCustomerContactRequest, 'customerId'>
  ): Promise<ContactDetailResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.createCustomerContact');
    const requestData: CreateCustomerContactRequest = {
      ...data,
      customerId,
    };

    const response = await apiClient.post<ContactDetailResponse>(
      ROUTES.CUSTOMER_CONTACTS(customerId),
      requestData
    );
    return response.data;
  },

  /**
   * Update vendor contact
   * PUT /api/vendors/:vendorId/contacts/:contactId
   */
  updateVendorContact: async (
    vendorId: number,
    contactId: number,
    data: UpdateMasterDataContactRequest
  ): Promise<ContactDetailResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.updateVendorContact');
    const response = await apiClient.put<ContactDetailResponse>(
      ROUTES.VENDOR_CONTACT(vendorId, contactId),
      data
    );
    return response.data;
  },

  /**
   * Update customer contact
   * PUT /api/customers/:customerId/contacts/:contactId
   */
  updateCustomerContact: async (
    customerId: number,
    contactId: number,
    data: UpdateMasterDataContactRequest
  ): Promise<ContactDetailResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.updateCustomerContact');
    const response = await apiClient.put<ContactDetailResponse>(
      ROUTES.CUSTOMER_CONTACT(customerId, contactId),
      data
    );
    return response.data;
  },

  /**
   * Toggle vendor contact status
   * PATCH /api/vendors/:vendorId/contacts/:contactId/toggle
   *
   * LIVE (CU-1b masters activation) — `vendorContactSetActive`, the SAME
   * root and the SAME document `contactApi.toggleStatus` rides, because it
   * is the same route. Only the declared response differs, so this side
   * narrows the shared echo onto `ToggleMasterDataContactResponse`.
   *
   * NOT `masterDataContactSetActive`: that root keys on a standalone
   * `contactId: ID!` with no party scope, and nothing in this codebase calls
   * the standalone route. Routing a party-scoped toggle through it would
   * change which row the write addresses.
   */
  toggleVendorContactStatus: async (
    vendorId: number,
    contactId: number,
    isActive: boolean
  ): Promise<ToggleMasterDataContactResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorContactSetActiveResult>(
        mastersOperations.vendorContactSetActive,
        { input: { vendorId: String(vendorId), contactId, isActive } }
      );
      throwOnUserErrors(
        result.vendorContactSetActive.userErrors,
        'vendorContactSetActive'
      );
      if (!result.vendorContactSetActive.contact) {
        throw new GraphQLTransportError({
          message: 'vendorContactSetActive returned neither a contact nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyContactToggleEcho(result.vendorContactSetActive.contact);
    }
    const response = await apiClient.patch<ToggleMasterDataContactResponse>(
      ROUTES.VENDOR_CONTACT_TOGGLE(vendorId, contactId),
      { isActive }
    );
    return response.data;
  },

  /**
   * Toggle customer contact status
   * PATCH /api/customers/:customerId/contacts/:contactId/toggle
   *
   * LIVE (CU-1b masters activation) — `customerContactSetActive`, the
   * customer twin of the method above, sharing its document with
   * `customerApi.toggleContactStatus` for the same reason.
   */
  toggleCustomerContactStatus: async (
    customerId: number,
    contactId: number,
    isActive: boolean
  ): Promise<ToggleMasterDataContactResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<CustomerContactSetActiveResult>(
        mastersOperations.customerContactSetActive,
        { input: { customerId: String(customerId), contactId, isActive } }
      );
      throwOnUserErrors(
        result.customerContactSetActive.userErrors,
        'customerContactSetActive'
      );
      if (!result.customerContactSetActive.contact) {
        throw new GraphQLTransportError({
          message: 'customerContactSetActive returned neither a contact nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyContactToggleEcho(result.customerContactSetActive.contact);
    }
    const response = await apiClient.patch<ToggleMasterDataContactResponse>(
      ROUTES.CUSTOMER_CONTACT_TOGGLE(customerId, contactId),
      { isActive }
    );
    return response.data;
  },

  /**
   * Set vendor contact as primary
   * PATCH /api/vendors/:vendorId/contacts/:contactId/set-primary
   */
  setVendorContactPrimary: async (
    vendorId: number,
    contactId: number,
    isPrimary: boolean
  ): Promise<SetPrimaryMasterDataContactResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.setVendorContactPrimary');
    const response = await apiClient.patch<SetPrimaryMasterDataContactResponse>(
      ROUTES.VENDOR_CONTACT_SET_PRIMARY(vendorId, contactId),
      { isPrimary }
    );
    return response.data;
  },

  /**
   * Set customer contact as primary
   * PATCH /api/customers/:customerId/contacts/:contactId/set-primary
   */
  setCustomerContactPrimary: async (
    customerId: number,
    contactId: number,
    isPrimary: boolean
  ): Promise<SetPrimaryMasterDataContactResponse> => {
    refuseUnobservableMastersWrite('masterDataContactApi.setCustomerContactPrimary');
    const response = await apiClient.patch<SetPrimaryMasterDataContactResponse>(
      ROUTES.CUSTOMER_CONTACT_SET_PRIMARY(customerId, contactId),
      { isPrimary }
    );
    return response.data;
  },

  /**
   * Get vendors list for dropdown
   * GET /api/vendors (with minimal fields)
   */
  getVendorsList: async (): Promise<VendorListItem[]> => {
    const response = await apiClient.get<{
      data: Array<{
        vendorId: number;
        vendorName: string;
        isActive: boolean;
      }>;
    }>(ROUTES.VENDORS, {
      params: {
        pageSize: -1, // Get all
        isActive: 1, // Active only
      },
    });
    return response.data.data.map((v) => ({
      vendorId: v.vendorId,
      vendorName: v.vendorName,
      isActive: v.isActive,
    }));
  },

  /**
   * Get customers list for dropdown
   * GET /api/customers (with minimal fields)
   */
  getCustomersList: async (): Promise<CustomerListItem[]> => {
    const response = await apiClient.get<{
      data: Array<{
        customerId: number;
        customerName: string;
        isActive: boolean;
      }>;
    }>(ROUTES.CUSTOMERS, {
      params: {
        pageSize: -1, // Get all
        isActive: 1, // Active only
      },
    });
    return response.data.data.map((c) => ({
      customerId: c.customerId,
      customerName: c.customerName,
      isActive: c.isActive,
    }));
  },
};

export default masterDataContactApi;
